import { useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowAltCircleLeft } from '@fortawesome/free-regular-svg-icons';
import { Card, Col, Container, Form, Row, Button } from 'react-bootstrap';
import { faPen, faSave } from '@fortawesome/free-solid-svg-icons';
import { useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '../template/store';
import { getCountryList, getDocumentListByLinerAndCountry, getLanguageList, getLinerList, getStatusList, getUploadDocDetails, submitDoc } from './actions/DocumentMasterActions';
import UploadThing from '../../../components/common/UploadThing';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { AESDecrypt, encryptAndEncode } from '../../../encrypt-util';
import { updateDashbaordRefresh } from '../../common/components/DataTable/slices/DataTable';
import useDataCountry from '../../../hooks/useDataCountry';
import { openDocumentInNewTab } from '../../../helpers/openDocumentInNewTab';

// Validation rules - defined outside component for performance
const VALIDATION_RULES = {
  helpDocument: { required: true, message: 'Please upload help document' },
  country: { required: true, message: 'Please select country' },
  line: { required: true, message: 'Please select line' },
  language: { required: true, message: 'Please select language' },
  moduleName: { required: true, message: 'Please enter module name' },
  sequenceNumber: { required: true, message: 'Please enter sequence number' },
  // videoPath: { required: true, message: 'Please enter video path' },
} as const;

// Types
interface ErrorType {
  helpDocument?: string;
  country?: string;
  line?: string;
  language?: string;
  moduleName?: string;
  sequenceNumber?: string;
  videoPath?: string;
  status?: string;
}

interface FormState {
  country: string;
  language: string;
  line: string;
  videoPath: string;
  moduleName: string;
  sequenceNumber: string;
  helpDocument: any;
  status: number | null;
  errors: ErrorType;
}

type FormAction =
  | { type: 'UPDATE_FIELD'; field: keyof Omit<FormState, 'errors'>; value: string }
  | { type: 'UPDATE_DOCUMENT'; doc: any }
  | { type: 'CLEAR_DOCUMENT' }
  | { type: 'SET_MULTIPLE_ERRORS'; errors: ErrorType }
  | { type: 'CLEAR_ALL_ERRORS' }
  | { type: 'RESET_FORM' };

interface SelectOption {
  text: string;
  value: string;
}

// Initial state
const initialState: FormState = {
  country: '',
  language: '',
  line: '',
  videoPath: '',
  moduleName: '',
  sequenceNumber: '',
  status: null,
  helpDocument: null,
  errors: {},
};

// Reducer
const formReducer = (state: FormState, action: FormAction): FormState => {
  switch (action.type) {
    case 'UPDATE_FIELD':
      return {
        ...state,
        [action.field]: action.value,
        errors: { ...state.errors, [action.field]: undefined }, // Clear error on update
      };
    case 'UPDATE_DOCUMENT':
      return {
        ...state,
        helpDocument: action.doc,
        errors: { ...state.errors, helpDocument: undefined },
      };
    case 'CLEAR_DOCUMENT':
      return { ...state, helpDocument: null };
    case 'SET_MULTIPLE_ERRORS':
      return { ...state, errors: action.errors };
    case 'CLEAR_ALL_ERRORS':
      return { ...state, errors: {} };
    case 'RESET_FORM':
      return initialState;
    default:
      return state;
  }
};

const UploadDocument = ({ backPath }: { backPath: string }) => {
  const [countryList, setCountryList] = useState<SelectOption[]>([]);
  const [statusList, setStatusList] = useState<SelectOption[]>([]);
  const [languageOptions, setLanguageOptions] = useState<SelectOption[]>([]);
  const [lineOptions, setLineOptions] = useState<SelectOption[]>([]);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const [state, dispatchReducer] = useReducer(formReducer, initialState);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();

  const dataCountry = useDataCountry();
  const mstDocumentId = AESDecrypt(location.pathname.split('/')[3] ?? '');

  const profileDetails = useSelector(
    (state: RootState) => state.userProfile.profile
  );
  const selectedLinerId = useSelector(
    (state: RootState) => state.navbarTop.linerId
  );

  console.log(languageOptions,"<<languageOptions")

  // Validation function
  const validateForm = (): boolean => {
    const errors: ErrorType = {};
    let hasError = false;

    // Collect all errors in a single pass
    (Object.keys(VALIDATION_RULES) as Array<keyof typeof VALIDATION_RULES>).forEach((field) => {
      const rule = VALIDATION_RULES[field];
      const value = state[field];

      if (rule.required && !value) {
        errors[field] = rule.message;
        hasError = true;
      }
    });


    // Batch dispatch all errors at once
    if (hasError) {
      dispatchReducer({ type: 'SET_MULTIPLE_ERRORS', errors });
    }

    return hasError;
  };

  const handleFieldChange = (field: keyof Omit<FormState, 'errors'>, value: string) => {
    dispatchReducer({ type: 'UPDATE_FIELD', field, value });
  };

  const handleDocumentUpload = (name: string, serverPath: string, serverName: string) => {
    dispatchReducer({
      type: 'UPDATE_DOCUMENT',
      doc: { name, serverPath, serverName },
    });
  };

  const handleDocumentFailed = () => {
    dispatchReducer({ type: 'CLEAR_DOCUMENT' });
  };

  const handleCountryChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const countryValue = e.target.value;
    handleFieldChange('country', countryValue);
    handleFieldChange('line', '');
    handleFieldChange('language', '');
    setLineOptions([]);
    setLanguageOptions([]);

    if (!countryValue) return;

    try {
      const linerData = await dispatch(getLinerList({ partnerCountry: countryValue })).unwrap();
      setLineOptions(linerData || []);

      if (linerData?.length === 1) {
        await handleLineChange(linerData[0].value, countryValue);
      }
    } catch (error) {
      console.error('Error fetching liner list:', error);
      setLineOptions([]);
    }
  };

  const handleLineChange = async (lineValue: string, selectedCountry?: string) => {
    const country = selectedCountry || state.country;
    handleFieldChange('line', lineValue);
    handleFieldChange('language', '');
    setLanguageOptions([]);

    if (!lineValue || !country) return;

    try {
      let langData = await dispatch(
        getLanguageList({ selectedLinerId: lineValue, partnerCountry: country })
      ).unwrap();

      if (!langData || langData.length === 0) {
        langData = [{ text: 'English', value: 'EN' }];
      }
      console.log(langData,"<<langData")
      setLanguageOptions(langData);

      if (langData.length === 1) {
        alert(langData[0].value);
        handleFieldChange('language', langData[0].value);
      }
    } catch (error) {
      console.error('Error fetching language list:', error);
      setLanguageOptions([{ text: 'English', value: 'EN' }]);
    }
  };

  const onSubmitForm = async () => {
    // Clear previous errors
    dispatchReducer({ type: 'CLEAR_ALL_ERRORS' });

    // Validate form
    if (validateForm()) return;

    try {
      const { helpDocument, line, language, country, moduleName, videoPath, sequenceNumber } = state ?? {
        helpDocument: null, line: '', language: '', country: '', moduleName: '', videoPath: '', sequenceNumber: null
      };

      const payload = {
        documentHelpId: mstDocumentId,
        appId: '1',
        linerId: line,
        languageCode: language,
        countryCode: country,
        module: moduleName,
        docPath: helpDocument?.serverPath ?? '',
        docName: helpDocument?.name ?? '',
        tempName: helpDocument?.serverName ?? '',
        videoPath,
        sequenceNumber,
        helpDocumentStatus: state.status ?? '1',
      };

      const res = await dispatch(submitDoc(payload)).unwrap();
      const isSuccess = res?.status === 'SUCCESS';
      const message =
        res?.message ||
        (isSuccess ? 'Document submitted successfully' : 'Document submission failed');

      toast[isSuccess ? 'success' : 'error'](message);

      if (isSuccess) {
        dispatch(updateDashbaordRefresh(true));
        const linerId=(profileDetails.partnerType === 'liner'
            ? profileDetails?.partnerId
            : selectedLinerId)?.toString();
        if(profileDetails?.partnerCountry && linerId)
        {
          dispatch(getDocumentListByLinerAndCountry({
            country: profileDetails?.partnerCountry,
            liner: linerId
          }))
        }
        dispatchReducer({ type: 'RESET_FORM' });
        navigate(backPath);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('An unexpected error occurred while submitting the document');
    }
  };

  const fetchStatusOptions = async () => {
    try {
      const data = await dispatch(getStatusList({
        linerId: selectedLinerId,
        partnerCountry: dataCountry
      })).unwrap();
      if (data && data.length > 0) {
        setStatusList(data);
      }
      else {
        setStatusList([]);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
      setCountryList([]);
    }
  }

  const fetchCountries = async () => {
    try {
      const data = await dispatch(getCountryList).unwrap();
      const countries = data?.map((c: any) => ({
        text: c.countryName,
        value: c.countryShortCode,
      })) || [];

      setCountryList(countries);

      if (countries.length === 1) {
        await handleCountryChange({ target: { value: countries[0].value } } as any);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
      setCountryList([]);
    }
  };

  const fetchDocDetails = async () => {

    try {
      const data: any = await dispatch(getUploadDocDetails(mstDocumentId)).unwrap();

      if (!data) return;
      setIsDisabled(true);
      handleFieldChange('country', data.countryCode || '');
      handleFieldChange('videoPath', data.videoPath || '');
      handleFieldChange('moduleName', data.moduleName || '');
      handleFieldChange('sequenceNumber', data.sequenceNumber?.toString() || '');
      console.log(data, '<<data')
      handleFieldChange("status", data.helpDocumentStatus);

      // Populate document if exists
      if (data.docName && data.docPath) {
        dispatchReducer({
          type: 'UPDATE_DOCUMENT',
          doc: {
            name: data.docName,
            serverPath: data.docPath,
            serverName: data.tempName || '',
          },
        });
      }

      // Fetch and populate liner options
      if (data.countryCode) {
        const linerData = await dispatch(
          getLinerList({ partnerCountry: data.countryCode})
        ).unwrap();
        setLineOptions(linerData || []);
        handleFieldChange('line', data.linerId || '');

        // Fetch and populate language options
        if (data.linerId) {
          let langData = await dispatch(
            getLanguageList({ selectedLinerId: data.linerId, partnerCountry: data.countryCode })
          ).unwrap();
          if (!langData || langData.length === 0) {
            langData = [{ text: 'English', value: '1' }];
          }

          setLanguageOptions(langData);
          handleFieldChange('language', data.languageCode.trim() || '1');
        }
      }
    } catch (error) {
      setIsDisabled(false);
      console.error('Error fetching document details:', error);
    }
  };

  // Initial load - fetch countries and document details in sequence
  useEffect(() => {
    const initializeForm = async () => {
      await fetchCountries();
      if (mstDocumentId) {
        await fetchDocDetails();
      }
      else {
        dispatchReducer({ type: 'RESET_FORM' });
      }
    };

    initializeForm();
  }, [mstDocumentId]);

  useEffect(() => {
    if (!dataCountry || !selectedLinerId) return;
    fetchStatusOptions();
  }, [dataCountry, selectedLinerId])

  return (
    <Card style={{ height: '100%', overflow: 'auto' }}>
      <Card.Header
        style={{ position: 'sticky', top: 0, zIndex: 1000, backgroundColor: 'white' }}
        className='border-bottom d-flex justify-content-between align-items-center'
      >
        <FontAwesomeIcon
          icon={faArrowAltCircleLeft}
          className='text-warning cursor-pointer fa-lg mb-1'
          onClick={() => navigate(backPath)}
        />
        <div className='row col-12'>
          <h2 className='mb-0 fs-0 text-primary'>{t('resource_2:uploadDoc')}</h2>
        </div>
      </Card.Header>

      <Card.Body>
        <Container>
          <Row className='gy-3'>
            {/* Country Select */}
            <Col lg={4}>
              <Form.Group controlId='country'>
                <span className='mandatory'>*</span>
                <Form.Label className='fs--1'>
                  {t('resource_1:country')}</Form.Label>
                <Form.Select
                  size='sm'
                  value={state.country}
                  onChange={handleCountryChange}
                  isInvalid={!!state.errors.country}
                  disabled={!!mstDocumentId}
                >
                  <option value='' hidden>
                    Select Country
                  </option>
                  {countryList.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.text}
                    </option>
                  ))}
                </Form.Select>
                {state.errors.country && (
                  <Form.Control.Feedback type='invalid' style={{ display: 'block' }}>
                    {state.errors.country}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            {/* Liner Select */}
            <Col lg={4}>
              <Form.Group controlId='line'>
                <span className='mandatory'>*</span>
                <Form.Label className='fs--1'>{t('resource_1:line')}</Form.Label>
                <Form.Select
                  size='sm'
                  value={state.line}
                  onChange={(e) => handleLineChange(e.target.value)}
                  isInvalid={!!state.errors.line}
                  disabled={!!mstDocumentId}
                >
                  <option value='' hidden>
                    Select Liner
                  </option>
                  {lineOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.text}
                    </option>
                  ))}
                </Form.Select>
                {state.errors.line && (
                  <Form.Control.Feedback type='invalid' style={{ display: 'block' }}>
                    {state.errors.line}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            {/* Language Select */}
            <Col lg={4}>
              <Form.Group controlId='language'>
                <span className='mandatory'>*</span>
                <Form.Label className='fs--1'>{t('resource_1:language')}</Form.Label>
                <Form.Select
                  size='sm'
                  value={state.language}
                  onChange={(e) => handleFieldChange('language', e.target.value)}
                  isInvalid={!!state.errors.language}
                  disabled={!!mstDocumentId}
                >
                  <option value='' hidden>
                    Select Language
                  </option>
                  {languageOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.text}
                    </option>
                  ))}
                </Form.Select>
                {state.errors.language && (
                  <Form.Control.Feedback type='invalid' style={{ display: 'block' }}>
                    {state.errors.language}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            {/* Video Path */}
            <Col lg={4}>
              <Form.Group controlId='videoPath'>
                {/* <span className='mandatory'>*</span> */}
                <Form.Label className='fs--1'>{t('resource_2:video_path')}</Form.Label>
                <Form.Control
                  size='sm'
                  value={state.videoPath}
                  onChange={(e) => handleFieldChange('videoPath', e.target.value)}
                  isInvalid={!!state.errors.videoPath}
                  disabled={isDisabled}
                />
                {state.errors.videoPath && (
                  <Form.Control.Feedback type='invalid' style={{ display: 'block' }}>
                    {state.errors.videoPath}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            {/* Module Name */}
            <Col lg={4}>
              <Form.Group controlId='moduleName'>
                <span className='mandatory'>*</span>
                <Form.Label className='fs--1'>{t('resource_2:module_name')}</Form.Label>
                <Form.Control
                  size='sm'
                  value={state.moduleName}
                  onChange={(e) => handleFieldChange('moduleName', e.target.value)}
                  isInvalid={!!state.errors.moduleName}
                  disabled={!!mstDocumentId}
                />
                {state.errors.moduleName && (
                  <Form.Control.Feedback type='invalid' style={{ display: 'block' }}>
                    {state.errors.moduleName}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            {/* Sequence Number */}
            <Col lg={4}>
              <Form.Group controlId='sequence_number'>
                <span className='mandatory'>*</span>
                <Form.Label className='fs--1'>{t('resource_2:sequence_number')}</Form.Label>
                <Form.Control
                  size='sm'
                  maxLength={3}
                  value={state.sequenceNumber}
                  onChange={(e) =>
                    handleFieldChange('sequenceNumber', e.target.value.replace(/[^0-9]/g, ''))
                  }
                  disabled={isDisabled}
                  isInvalid={!!state.errors.sequenceNumber}
                />
                {state.errors.sequenceNumber && (
                  <Form.Control.Feedback type='invalid' style={{ display: 'block' }}>
                    {state.errors.sequenceNumber}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            {/* Status Select */}
            {
              (mstDocumentId) &&
              <Col lg={4}>
                <Form.Group controlId='status'>
                  <Form.Label className='fs--1'>{t('resource_1:status')}</Form.Label>
                  <Form.Select
                    size='sm'
                    value={state.status ?? ''}
                    onChange={(e) => handleFieldChange('status', e.target.value)}
                    isInvalid={!!state.errors.status}
                    disabled={!!isDisabled}
                  >
                    <option value='' hidden>
                      Select Status
                    </option>
                    {statusList.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.text}
                      </option>
                    ))}
                  </Form.Select>
                  {state.errors.status && (
                    <Form.Control.Feedback type='invalid' style={{ display: 'block' }}>
                      {state.errors.status}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            }


            {/* Help Document Upload */}
            <Col lg={4}>
              <Form.Group controlId='helpDocument'>
                <span className='mandatory'>*</span>
                <Form.Label className='fs--1'>{t('resource_2:helpDocument')}</Form.Label>
                {
                  ((mstDocumentId && isDisabled)) ?
                    <div className='d-flex justify-content-start align-items-center'>
                      <span
                        className='text-primary cursor-pointer fs--1'
												onClick={async () => {
												  await openDocumentInNewTab({
												    url: '/api/master/v1/common/openDocument',
												    params: {
												      id: encryptAndEncode(mstDocumentId),
												      moduleTp: 'HELP_DOC',
												      linerId: encryptAndEncode(state.line),
												    },
												  });
												}}>
                        {state.helpDocument?.name}
                      </span>
                    </div>
                    :
                    <UploadThing
                      name={state.helpDocument?.name || ''}
                      loading={0}
                      fulfilled={handleDocumentUpload}
                      failed={handleDocumentFailed}
                      pending={(loading: number) => console.log('Upload progress:', loading)}
                      clear={() => dispatchReducer({ type: 'CLEAR_DOCUMENT' })}
                      selected={(name: string) => console.log('File selected:', name)}
                      fileLimit={10}
                      fileType={['PDF']}
                      labelWidth='250px'
                      allowRemove={false}
                    />
                }
                {state.errors.helpDocument && (
                  <div className='text-danger mt-1' style={{ fontSize: '0.875em' }}>
                    {state.errors.helpDocument}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row className='d-flex justify-content-center mt-4'>
            {
              isDisabled ?
                <Button
                  size='sm'
                  variant='falcon-primary'
                  style={{ width: 'max-content' }}
                  onClick={() => setIsDisabled(false)}
                >
                  <FontAwesomeIcon icon={faPen} color='#F5803E' className='me-2' />
                  Edit
                </Button> :
                <Button
                  size='sm'
                  variant='falcon-primary'
                  style={{ width: 'max-content' }}
                  onClick={onSubmitForm}
                >
                  <FontAwesomeIcon icon={faSave} className='me-2' />
                  Submit
                </Button>
            }

          </Row>
        </Container>
      </Card.Body>
    </Card>
  );
};

export default UploadDocument;